import { apiRequest } from "@/lib/api-request";
import type { TobisPersonRef } from "@/lib/tobis";

// Tobis Labor "Ergebnis"-Schritt: EIN KI-Call waehlt aus dem Kontext (Filmografien
// der genannten Personen + Themen-Pool + namentlich erkannte Titel - alles holt
// sich TobisPitchController.findComparables selbst ueber TobisClient) die
// wirklich vergleichbaren Titel aus und liefert direkt eine Potenzial-
// Einschaetzung. Ersetzt tobis-lab-pitch-compare.ts (ein Call pro Kandidat) und
// tobis-lab-pitch-assessment.ts (separater Einschaetzungs-Call) - Marvins Vorgabe
// 2026-08-06: Vergleichsauswahl und Potenzial-Einschaetzung sind fachlich EIN
// Schritt. Proxy-Aufruf zur BFF (/ai/tobis/find-comparables -> agent-service),
// bewusst NICHT in lib/tobis.ts (das ist tobis-api/* auf der eigenen
// REST-Schiene) - diese Route laeuft ueber den Assistenten-Transport
// (agent-proxy, ADR-0018).
//
// SSE statt eines einzelnen JSON-Response (Marvin, 2026-08-07: "eine echte
// Progress-Leiste, welche Daten wie gesammelt werden") - ein eigener, kleiner
// Frame-Parser statt lib/agent-stream.ts' postStream/pumpSse: die dortige
// AgentEvent-Form (delta/sessionId/message/suggestions) und der hart codierte
// "done"-Terminalwert passen nicht zu diesen Fortschritts-Events
// (stage_started/stage_done/result/error), und beide generisch zu machen waere
// ein Umweg auf geteilter, schon erprobter Chat-Streaming-Infrastruktur fuer
// diesen einen Tobis-Prototyp-Endpunkt.

export interface FindComparablesInput {
  pitch: string;
  genres?: string[];
  languages?: string[];
  /** Grenzt den Themen-Pool auf ein Kinostart-Fenster ein, siehe fetchThemePool im agent-service. */
  yearFrom?: string;
  yearTo?: string;
  director?: TobisPersonRef;
  writer?: TobisPersonRef;
  cast?: TobisPersonRef[];
  tone?: string;
  scriptSummary?: string;
  /** Nutzer-Schaetzung fuers eigene Projekt, USD wie TobisTitle.budgetUsd/revenueUsd - optional,
   *  wird der KI gegen die echten Vergleichstitel-Zahlen zur Einordnung gegeben. */
  expectedBudgetUsd?: string;
  expectedRevenueUsd?: string;
  /** TMDB-Keyword-Ids (searchKeywords) - der Themen-Pool, den findComparables selbst abruft. */
  keywordIds?: string[];
  /** Im Pitch-Text namentlich erkannte Titel (resolveTitles) - immer beruecksichtigt. */
  pinnedTitleIds?: string[];
  /**
   * "TOBIS Algorithmus"-Modus (Marvin, 2026-08-07): erzwingt statt der freien yearFrom/yearTo
   * ein 12-Jahre-Fenster, filtert den Themen-Pool auf DE-Kinostart ODER Streaming-Original und
   * laesst 5-15 statt bis zu 8 Vergleichsfilme zu - die Spec-Vorgabe fuer den Film-
   * Einschaetzungs-Agenten. Default aus (frei waehlbare Kriterien wie bisher).
   */
  tobisAlgorithm?: boolean;
  /**
   * "Nur diese Vergleichsfilme" (2026-10-06): pinnedTitleIds sind der komplette Vergleichssatz.
   * Das Modell bewertet genau diese Titel, ohne Themen-Pool, Graph-Nachbarn und Pflichtfilme je
   * Besetzungsperson. Ohne angepinnte Titel wirkungslos.
   */
  fixedComparables?: boolean;
}

export interface SelectedComparable {
  id: string;
  reasoning: string;
}

export interface FindComparablesResult {
  selected: SelectedComparable[];
  positive: string | null;
  negative: string | null;
  fazit: string | null;
  suitability: number | null;
  context: string | null;
}

/** Ein echter Zwischenstand aus TobisPitchController.runFindComparables - kein simulierter Timer.
 *  `stage` ist der Schluessel (z.B. "filmography:tmdb:31", "themepool", "pinned", "model"),
 *  `label` (nur bei stage_started) der Anzeigetext auf Deutsch, den der agent-service mitschickt. */
export interface FindComparablesProgressEvent {
  kind: "stage_started" | "stage_done";
  stage: string;
  label?: string;
}

// SSE-Frame-Trenner: eine Leerzeile, in jeder erlaubten Zeilenenden-Form.
const FRAME_SEP = /\r\n\r\n|\r\r|\n\n/;

function dataLines(frame: string): string {
  return frame
    .split(/\r\n|\r|\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("\n");
}

export async function findComparables(
  input: FindComparablesInput,
  onProgress?: (event: FindComparablesProgressEvent) => void,
): Promise<FindComparablesResult> {
  const { response } = await apiRequest("ai/tobis/find-comparables", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(input),
  });
  if (!response.ok || !response.body) throw new Error(`find-comparables → ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: FindComparablesResult | null = null;
  let errorMessage: string | null = null;

  // kotlinx' sealed-interface-Serialisierung legt den Discriminator standardmaessig unter
  // "type" ab (siehe agent-stream.ts' gleichlautender Kommentar zu AgentStreamEvent) - die
  // "kind"-Property auf FindComparablesStreamEvent existiert nur fuer die SSE-event:-Zeile.
  function handleFrame(frame: string) {
    const data = dataLines(frame);
    if (!data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return; // Heartbeat-Kommentar oder abgeschnittenes Teilstueck, kein echter Frame.
    }
    const type = (parsed as { type?: string }).type;
    if (type === "stage_started") {
      const p = parsed as { stage: string; label: string };
      onProgress?.({ kind: "stage_started", stage: p.stage, label: p.label });
    } else if (type === "stage_done") {
      const p = parsed as { stage: string };
      onProgress?.({ kind: "stage_done", stage: p.stage });
    } else if (type === "result") {
      const p = parsed as { response: FindComparablesResult };
      result = p.response;
    } else if (type === "error") {
      const p = parsed as { message: string };
      errorMessage = p.message;
    }
  }

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let m: RegExpExecArray | null;
    while ((m = FRAME_SEP.exec(buffer)) !== null) {
      handleFrame(buffer.slice(0, m.index));
      buffer = buffer.slice(m.index + m[0].length);
    }
  }
  if (buffer.trim()) handleFrame(buffer);

  if (errorMessage) throw new Error(errorMessage);
  if (!result) throw new Error("find-comparables stream ended without a result");
  return result;
}
